/**
 * Candy helpers for the sea tiers (Cell, Starfish, Jellyfish, Fish), on top of candy-kit.js.
 *
 *   seaFace       candyFace plus the sea tiers' own resting faces: "cheeky" (Starfish: a lopsided
 *                 grin with the tongue stuck out), "dreamy" (Jellyfish: starry eyes drifting up
 *                 with heart catchlights, long lashes, brows lifted "aww", a soft little smile)
 *                 and "toothy" (Cell: a big grin with two bunny teeth). Every kit mood still
 *                 works on top, and "blink" closes only the eyes and keeps the resting mouth.
 *   sdfOutline    the smooth closed outline (unit points) of a signed-distance shape, for one-piece
 *                 bodies blended from capsules (the Starfish's star-jump), plus capsuleSdf / smin.
 *   candyStripes  candy-cane bands across a tube, painted inside a part's clip.
 *   glowDot       a soft glow (radial gradient, no shadowBlur) under a glowing pearl.
 *
 * All sizes are in units of the disc radius r unless noted. Deterministic, no filters.
 */
import { TAU, sparklePath } from "../toon-kit.js";
import {
  rgba,
  MOODS,
  candyEye,
  candyMouth,
  IRIS,
  EYE_INK,
  MOUTH_IN,
  TONGUE,
  FACE_PEN,
  dotSpec,
  heartPath,
} from "./candy-kit.js";

// ---------------------------------------------------------------------------------------
// Faces.

/** The sea tiers' resting faces → [eyes, mouth] (kit MOODS cover every other mood). */
export var SEA_RESTS = {
  cheeky: ["open", "tongue"],
  dreamy: ["dreamy", "dreamy"],
  toothy: ["open", "toothy"],
};

function faceKinds(mood, rest) {
  if (mood === rest) return SEA_RESTS[rest] || MOODS[rest] || MOODS.smile;
  return MOODS[mood] || SEA_RESTS[mood] || MOODS.smile;
}

/**
 * A candy face centred between the eyes at (x, y), like candy-kit candyFace (same spec: eye,
 * gap, mouth, drop, mouthAt, ink, iris, blush, blushAlpha, cheek, lashes, clip, look, wink,
 * fang, lid) plus `dreamLook` ([dx, dy] in eye radii: where dreamy eyes gaze) and `tongueSide`
 * (−1 / 1: which side the cheeky tongue hangs). `rest` may be a kit mood or a SEA_RESTS key.
 * Returns { mouthMood, mx, my, mw, eyeR }.
 */
export function seaFace(c, r, x, y, spec, pose, rest) {
  var sp = spec || {};
  var mood = (pose && pose.mood) || rest;
  var restKinds = faceKinds(rest, rest);
  var kinds = faceKinds(mood, rest);
  var eyeKind = kinds[0];
  var mouthKind = kinds[1] || restKinds[1];
  var restMouth = mouthKind === restKinds[1];
  var e = (sp.eye != null ? sp.eye : 0.13) * r;
  var gap = (sp.gap != null ? sp.gap : 0.38) * r;
  var mw = (sp.mouth != null ? sp.mouth : 0.085) * r;
  var drop = sp.drop != null ? sp.drop : 1.6;
  var mx = sp.mouthAt && restMouth ? sp.mouthAt[0] * r : x;
  var my = sp.mouthAt && restMouth ? sp.mouthAt[1] * r : y + e * drop;

  c.save();
  if (sp.clip) {
    c.beginPath();
    sp.clip(c);
    c.clip();
  }
  // Glossy cheeks: a soft pink pool and a crisp dot of shine (as candyFace).
  var ck = sp.cheek || [0.98, 1.25];
  for (var b = -1; b <= 1; b += 2) {
    var bx = x + b * gap * ck[0], by = y + e * ck[1];
    c.fillStyle = rgba(sp.blush || "#ff5aa8", sp.blushAlpha || 0.66);
    c.beginPath();
    c.ellipse(bx, by, e * 0.95, e * 0.58, 0, 0, TAU);
    c.fill();
    c.fillStyle = "rgba(255,255,255,0.85)";
    c.beginPath();
    c.ellipse(bx - e * 0.32, by - e * 0.16, e * 0.17, e * 0.11, -0.4, 0, TAU);
    c.fill();
  }
  if (mouthKind !== "tongue") seaMouth(c, r, mx, my, mw, mouthKind, sp);
  c.restore();
  // The cheeky tongue may hang past the head's edge, so it is drawn unclipped.
  if (mouthKind === "tongue") seaMouth(c, r, mx, my, mw, mouthKind, sp);

  for (var side = -1; side <= 1; side += 2) {
    var kind = eyeKind;
    if (sp.wink === side && mood === rest) kind = "wink";
    var look = kind === "dreamy" ? sp.dreamLook || sp.look : sp.look;
    var lx = look ? look[0] * e : 0, ly = look ? look[1] * e : 0;
    if (kind === "dreamy") dreamyEye(c, r, x + side * gap * 0.5, y, e, side, sp, lx, ly);
    else candyEye(c, r, x + side * gap * 0.5 + lx, y + ly, e, kind, side, sp);
  }
  return { mouthMood: mouthKind, mx: mx, my: my, mw: mw, eyeR: e };
}

/** A sea mouth: "tongue", "toothy", "dreamy", or any candy-kit mouth. */
export function seaMouth(c, r, x, y, w, kind, sp) {
  if (kind === "tongue") tongueMouth(c, r, x, y, w, sp);
  else if (kind === "toothy") toothyMouth(c, r, x, y, w, sp);
  else if (kind === "dreamy") dreamyMouth(c, r, x, y, w, sp);
  else candyMouth(c, r, x, y, w, kind, sp);
}

/**
 * Cheeky: a lopsided open grin (one corner hitched up) with the tongue stuck out over the lower
 * lip on `sp.tongueSide`, a groove down its middle and a wet dot of shine.
 */
function tongueMouth(c, r, x, y, w, sp) {
  var ink = sp.ink || EYE_INK;
  var ts = sp.tongueSide || 1;
  var lw = FACE_PEN * r;
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = ink;
  c.lineWidth = lw;
  var lx = x - w, ly = y - w * (ts > 0 ? 0.32 : 0.08);
  var rx = x + w, ry = y - w * (ts > 0 ? 0.08 : 0.32);
  var shape = function (cc) {
    cc.moveTo(lx, ly);
    cc.quadraticCurveTo(x, y + w * 0.12, rx, ry);
    cc.bezierCurveTo(x + w * 0.92, y + w * 1.05, x - w * 0.92, y + w * 1.05, lx, ly);
    cc.closePath();
  };
  c.beginPath();
  shape(c);
  c.fillStyle = MOUTH_IN;
  c.fill();
  c.stroke();
  // The tongue: a soft U from inside the mouth hanging past the lip, leaning outward.
  var tx = x + ts * w * 0.24, ty = y + w * 0.36;
  var tw = w * 0.4, th = w * 1.12;
  c.translate(tx, ty);
  c.rotate(-ts * 0.18);
  var u = function (cc) {
    cc.moveTo(-tw, 0);
    cc.lineTo(-tw, th - tw);
    cc.arc(0, th - tw, tw, Math.PI, 0, true);
    cc.lineTo(tw, 0);
  };
  c.beginPath();
  u(c);
  c.closePath();
  c.fillStyle = TONGUE;
  c.fill();
  c.fillStyle = "rgba(255,255,255,0.35)";
  c.beginPath();
  c.ellipse(-tw * 0.35, th * 0.55, tw * 0.38, th * 0.28, 0, 0, TAU);
  c.fill();
  c.lineWidth = lw * 0.85;
  c.beginPath();
  u(c);
  c.stroke();
  c.strokeStyle = rgba(MOUTH_IN, 0.55);
  c.lineWidth = lw * 0.6;
  c.beginPath();
  c.moveTo(0, th * 0.28);
  c.lineTo(0, th * 0.68);
  c.stroke();
  dotSpec(c, -tw * 0.42, th * 0.5, tw * 0.22);
  c.restore();
}

/** Toothy: an open grin with two square bunny teeth under the top lip and a tongue below. */
function toothyMouth(c, r, x, y, w, sp) {
  var ink = sp.ink || EYE_INK;
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = ink;
  c.lineWidth = FACE_PEN * r;
  var shape = function (cc) {
    cc.moveTo(x - w, y - w * 0.2);
    cc.quadraticCurveTo(x, y + w * 0.05, x + w, y - w * 0.2);
    cc.bezierCurveTo(x + w * 0.95, y + w * 1.2, x - w * 0.95, y + w * 1.2, x - w, y - w * 0.2);
    cc.closePath();
  };
  c.beginPath();
  shape(c);
  c.fillStyle = MOUTH_IN;
  c.fill();
  c.save();
  c.clip();
  c.fillStyle = TONGUE;
  c.beginPath();
  c.ellipse(x + w * 0.05, y + w * 1.0, w * 0.62, w * 0.46, 0, 0, TAU);
  c.fill();
  // Two bunny teeth hanging from the lip, with a gap line between them.
  c.fillStyle = "#ffffff";
  c.beginPath();
  c.roundRect(x - w * 0.36, y - w * 0.3, w * 0.72, w * 0.62, w * 0.14);
  c.fill();
  c.restore();
  c.lineWidth = FACE_PEN * r * 0.55;
  c.beginPath();
  c.moveTo(x, y - w * 0.04);
  c.lineTo(x, y + w * 0.28);
  c.moveTo(x - w * 0.34, y + w * 0.05);
  c.lineTo(x - w * 0.34, y + w * 0.3);
  c.quadraticCurveTo(x - w * 0.34, y + w * 0.34, x - w * 0.28, y + w * 0.34);
  c.lineTo(x + w * 0.28, y + w * 0.34);
  c.quadraticCurveTo(x + w * 0.34, y + w * 0.34, x + w * 0.34, y + w * 0.3);
  c.lineTo(x + w * 0.34, y + w * 0.05);
  c.stroke();
  c.lineWidth = FACE_PEN * r;
  c.beginPath();
  shape(c);
  c.stroke();
  c.restore();
}

/** Dreamy: a soft little open smile, rounder than the kit grin, with a tongue tip. */
function dreamyMouth(c, r, x, y, w, sp) {
  var ink = sp.ink || EYE_INK;
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = ink;
  c.lineWidth = FACE_PEN * r;
  var hw = w * 0.72;
  var shape = function (cc) {
    cc.moveTo(x - hw, y);
    cc.quadraticCurveTo(x, y + w * 0.16, x + hw, y);
    cc.bezierCurveTo(x + hw * 0.9, y + w * 1.05, x - hw * 0.9, y + w * 1.05, x - hw, y);
    cc.closePath();
  };
  c.beginPath();
  shape(c);
  c.fillStyle = MOUTH_IN;
  c.fill();
  c.save();
  c.clip();
  c.fillStyle = TONGUE;
  c.beginPath();
  c.ellipse(x, y + w * 0.86, w * 0.5, w * 0.36, 0, 0, TAU);
  c.fill();
  c.restore();
  c.beginPath();
  shape(c);
  c.stroke();
  c.restore();
}

/**
 * A dreamy (starry-eyed) eye: the sparkly plum eye with its pupil drifting up (`lx`, `ly` move
 * only the iris and pupil), a big heart for a catchlight, a twinkle and a dot, long lashes
 * curling off the outer corner and a soft brow lifted at the inner end ("aww").
 */
export function dreamyEye(c, r, x, y, e, side, sp, lx, ly) {
  var ry = e * 1.2;
  var iris = sp.iris || IRIS.pink;
  var lw = FACE_PEN * r;
  c.save();
  c.fillStyle = EYE_INK;
  c.beginPath();
  c.ellipse(x, y, e, ry, 0, 0, TAU);
  c.fill();
  c.clip();
  var ix = x + (lx || 0), iy = y + (ly || 0);
  c.fillStyle = iris.base;
  c.beginPath();
  c.ellipse(ix, iy + ry * 0.1, e * 0.8, ry * 0.8, 0, 0, TAU);
  c.fill();
  c.fillStyle = iris.light;
  c.beginPath();
  c.ellipse(x, y + ry * 0.66, e * 0.62, ry * 0.36, 0, 0, TAU);
  c.fill();
  c.fillStyle = EYE_INK;
  c.beginPath();
  c.ellipse(ix, iy - ry * 0.06, e * 0.56, ry * 0.58, 0, 0, TAU);
  c.fill();
  c.restore();
  // Catchlights: a big heart upper left, a twinkle lower right, a dot.
  c.fillStyle = "#ffffff";
  c.beginPath();
  heartPath(c, x - e * 0.28, y - ry * 0.32, e * 0.42, -0.25);
  c.fill();
  c.beginPath();
  sparklePath(c, x + e * 0.38, y + ry * 0.32, e * 0.34, 0);
  c.fill();
  c.beginPath();
  c.arc(x - e * 0.48, y + ry * 0.38, e * 0.1, 0, TAU);
  c.fill();
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = EYE_INK;
  // Long lashes sweeping up and out off the top outer edge.
  c.lineWidth = lw * 0.9;
  c.beginPath();
  var ex = x + side * e * 0.62, ey = y - ry * 0.84;
  c.moveTo(ex, ey);
  c.quadraticCurveTo(ex + side * e * 0.25, ey - e * 0.12, ex + side * e * 0.36, ey - e * 0.46);
  c.moveTo(x + side * e * 0.92, y - ry * 0.5);
  c.quadraticCurveTo(x + side * e * 1.2, y - ry * 0.56, x + side * e * 1.38, y - ry * 0.86);
  c.stroke();
  if (sp.brow !== false) {
    // Inner end lifted: a soft, longing "aww".
    c.strokeStyle = sp.ink || EYE_INK;
    c.lineWidth = lw * 0.95;
    c.beginPath();
    c.moveTo(x - side * e * 0.7, y - ry * 1.48);
    c.quadraticCurveTo(x + side * e * 0.05, y - ry * 1.68, x + side * e * 0.8, y - ry * 1.4);
    c.stroke();
  }
  c.restore();
}

// ---------------------------------------------------------------------------------------
// Signed-distance bodies.

/** Signed distance from (px, py) to a tapered capsule from a (radius ra) to b (radius rb). */
export function capsuleSdf(px, py, ax, ay, ra, bx, by, rb) {
  var dx = bx - ax, dy = by - ay, h = Math.hypot(dx, dy) || 1e-6;
  var ux = dx / h, uy = dy / h;
  var qx = px - ax, qy = py - ay;
  var along = qx * ux + qy * uy;
  var across = Math.abs(qx * uy - qy * ux);
  var b = (ra - rb) / h, a = Math.sqrt(Math.max(0, 1 - b * b));
  var k = -b * across + a * along;
  if (k < 0) return Math.hypot(across, along) - ra;
  if (k > a * h) return Math.hypot(across, along - h) - rb;
  return across * a + along * b - ra;
}

/** Polynomial smooth minimum: a union of two distances with a fillet of size k. */
export function smin(a, b, k) {
  var h = Math.max(k - Math.abs(a - b), 0) / k;
  return Math.min(a, b) - h * h * k * 0.25;
}

/**
 * The outline of the shape sdf(x, y) < 0 seen from the origin: n rays, each marched in from
 * `reach` to the outermost crossing and refined by bisection. Unit points, for smoothPath.
 */
export function sdfOutline(sdf, n, reach) {
  var pts = [];
  var step = 0.01;
  for (var i = 0; i < n; i++) {
    var a = (i / n) * TAU, ca = Math.cos(a), sa = Math.sin(a);
    var hi = reach, lo = reach;
    while (lo > 0 && sdf(lo * ca, lo * sa) > 0) {
      hi = lo;
      lo -= step;
    }
    for (var k = 0; k < 22; k++) {
      var m = (lo + hi) / 2;
      if (sdf(m * ca, m * sa) > 0) hi = m; else lo = m;
    }
    var d = (lo + hi) / 2;
    pts.push([d * ca, d * sa]);
  }
  return pts;
}

// ---------------------------------------------------------------------------------------
// Stripes and glows.

/**
 * Candy-cane bands across a tube (call inside a part's clip): along the unit centreline `pts`,
 * a band every `every` (r units of arc length), slanted by `slant`, `width` thick, in `color`.
 * Each band is a short stroke `reach` long either side of the centreline, so it crosses only
 * its own stretch of tube, never a neighbouring coil.
 */
export function candyStripes(c, r, pts, every, width, color, slant, reach) {
  c.save();
  c.strokeStyle = color;
  c.lineWidth = width * r;
  c.lineCap = "butt";
  var L = reach || 0.06, k = slant != null ? slant : 0.5;
  var next = every * 0.5, run = 0;
  for (var i = 1; i < pts.length; i++) {
    var dx = pts[i][0] - pts[i - 1][0], dy = pts[i][1] - pts[i - 1][1], d = Math.hypot(dx, dy) || 1e-6;
    while (run + d >= next) {
      var u = (next - run) / d;
      var px = pts[i - 1][0] + dx * u, py = pts[i - 1][1] + dy * u;
      var tx = dx / d, ty = dy / d, nx = -ty, ny = tx;
      c.beginPath();
      c.moveTo((px + (nx + tx * k) * L) * r, (py + (ny + ty * k) * L) * r);
      c.lineTo((px - (nx + tx * k) * L) * r, (py - (ny + ty * k) * L) * r);
      c.stroke();
      next += every;
    }
    run += d;
  }
  c.restore();
}

/** A soft round glow of `color` at (x, y) (absolute), radius s. */
export function glowDot(c, x, y, s, color, alpha) {
  var g = c.createRadialGradient(x, y, 0, x, y, s);
  g.addColorStop(0, rgba(color, alpha != null ? alpha : 0.75));
  g.addColorStop(0.45, rgba(color, (alpha != null ? alpha : 0.75) * 0.4));
  g.addColorStop(1, rgba(color, 0));
  c.fillStyle = g;
  c.beginPath();
  c.arc(x, y, s, 0, TAU);
  c.fill();
}
